import axios from "axios";
import { useState } from "react";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import EntrepreneurTermsNConditions from "./EntrepreneurTermsNConditions";
import Footer from "./Footer";
import InvestorConsent from "./InvestorConsent";
import TopBar from "./Topbar";

const accountActionClass =
    "rounded-[6px] bg-gold px-5 py-3 font-semibold text-navy shadow-sm transition hover:brightness-105";
const primaryActionClass =
    "rounded-[6px] bg-navy px-5 py-3 font-semibold text-paper shadow-sm transition hover:bg-navy-2";
const secondaryActionClass =
    "rounded-[6px] border border-navy bg-white px-5 py-3 font-semibold text-navy transition hover:bg-paper";
const affirmativeOptionClass =
    "w-full max-w-md rounded-[6px] bg-navy px-5 py-3 font-body font-semibold text-paper transition hover:bg-navy/90";
const negativeOptionClass =
    "w-full max-w-md rounded-[6px] border border-navy bg-white px-5 py-3 font-body font-semibold text-navy transition hover:bg-paper";

const LandingPage = () => {
    const [showModal, setShowModal] = useState(false);
    const [modalStep, setModalStep] = useState(1);
    const [showInvestorModal, setShowInvestorModal] = useState(false);
    const [investorModalStep, setInvestorModalStep] = useState(1);
    const [showOAuthSignupBanner, setShowOAuthSignupBanner] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    React.useEffect(() => {
        axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/auth/oauth/pending-registration`,
            { withCredentials: true },
        ).then(({ data }) => {
            if(data.pending) setShowOAuthSignupBanner(true);
        }).catch(() => {
            // The query parameter still controls the initial banner if this check fails.
        });
    }, []);


    // Helper: check if user is logged in (stricter)
    const isUserLoggedIn = () => {
        try {
            const user = JSON.parse(localStorage.getItem("user") || "null");
            if (user && (user.id || user.userId || user.email)) return true;
        } catch (e) {
            // ignore parse errors
        }

        // Fallback to explicit flag only — avoid treating stray tokens/ids as active session
        return localStorage.getItem("userLoggedIn") === "true";
    };

    // Helper: logout previous session
    const logoutPreviousSession = () => {
        localStorage.removeItem("userLoggedIn");
        localStorage.removeItem("session_id");
        localStorage.removeItem("jwtToken");
        localStorage.removeItem("user");
        localStorage.removeItem("userId");
        localStorage.removeItem("userEmail");
        // Add any other keys as needed
    };

    const handleInvestorClick = () => {
        if(isUserLoggedIn()) {
            const ok = window.confirm("Continuing will expire your previous session. Are you sure you want to continue?");
            if(!ok) return;
            logoutPreviousSession();
            setShowInvestorModal(true);
            localStorage.setItem("openModal", "investor");
            window.location.reload();
            return;
        }
        setShowInvestorModal(true);
        setInvestorModalStep(1);
    };

    const handleAnswer1 = (UsCitizen) => {
        if(UsCitizen) {
            // Navigate to the new investor welcome page instead of showing modal
            navigate("/investor-welcome");
        } else {
            setInvestorModalStep(3);
        }
    };

    
    // On mount or navigation, open any requested signup modal.
    React.useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const oauthError = searchParams.get("error");

        if(oauthError === "oauth_signup_required") {
            setShowOAuthSignupBanner(true);
            window.history.replaceState({}, "", "/sign-up");
        }

        const shouldOpenInvestorModal =
            location.state?.openInvestorModal ||
            searchParams.get("openInvestorModal") === "true";
        const shouldOpenEntrepreneurModal =
            location.state?.openEntrepreneurModal ||
            searchParams.get("openEntrepreneurModal") === "true";
        const modalToOpen = localStorage.getItem("openModal");

        if(modalToOpen === "investor") {
            setShowInvestorModal(true);
            setInvestorModalStep(1);
            setShowModal(false);
            localStorage.removeItem("openModal");
        } else if(modalToOpen === "entrepreneur") {
            setShowModal(true);
            setModalStep(1);
            setShowInvestorModal(false);
            localStorage.removeItem("openModal");
        } else if(shouldOpenInvestorModal) {
            setShowInvestorModal(true);
            setInvestorModalStep(1);
            setShowModal(false);
        } else if(shouldOpenEntrepreneurModal) {
            setShowModal(true);
            setModalStep(1);
            setShowInvestorModal(false);
        }
    }, [location.search, location.state]);
    const handleEntrepreneurClick = () => {
        if(isUserLoggedIn()) {
            const ok = window.confirm("Continuing will expire your previous session. Are you sure you want to continue?");
            if(!ok) return;
            logoutPreviousSession();
            setShowModal(true);
            return;
        }
        setShowModal(true);
    };

    const handleAnswer = (isWomanOwned) => {
        if(isWomanOwned) {
            setModalStep(2);
        } else {
            setModalStep(3);
        }
    };
    const handleGoHome = () => {
        setShowModal(false);
        navigate("/");
    };
    const handleChecklistYes = () => {
        setModalStep(5);
    };
    const handleFinalContinue = () => {
        setModalStep(4);
    };

    const bigModal = "w-full max-w-4xl max-h-[85vh] overflow-y-auto";
    const RegModal =
        "w-full max-w-4xl min-h-[70vh] max-h-[80vh] overflow-y-auto";
    const smallModal = "w-full max-w-sm  md:max-w-md  lg:max-w-lg";
    return (
        <>
            <TopBar />
            {showOAuthSignupBanner && (
                <div role="status" className="border-b border-yellow-300 bg-yellow-100 px-4 py-4">
                    <div className="mx-auto max-w-6xl">
                        <h2 className="text-center text-lg font-semibold italic text-yellow-900 font-poppins">
                            Looks like you&apos;re new here! Continue your registration by choosing your path below to complete your account creation.
                        </h2>
                    </div>
                </div>
            )}
            <div className="bg-paper font-body">
                {/* Standard public-page header */}
                <section className="relative w-full overflow-hidden bg-navy px-6 py-12 sm:py-16">
                    <div
                        className="absolute inset-0 opacity-[0.07]"
                        style={{
                            backgroundImage:
                                "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
                            backgroundSize: "24px 24px",
                        }}
                    />
                    <div className="relative mx-auto max-w-5xl text-center">
                        <h1 className="font-display text-3xl font-bold leading-tight text-paper md:text-[2.3rem]">
                            Join the AQi Platform
                        </h1>
                    </div>

                    <div className="relative mx-auto mt-5 max-w-4xl text-center">
                        <div className="mx-auto max-w-4xl">
                            <p className="font-display text-base font-semibold leading-relaxed text-paper/75 md:text-lg">
                                A platform designed for female founders bringing
                                new ideas to market.
                            </p>
                            <p className="mx-auto mt-4 max-w-3xl text-sm leading-relaxed text-paper/90 md:text-base">
                                Whether you’re here to explore Reg CF offerings
                                or to raise capital for your business,{" "}
                                AQi provides a platform designed to support your
                                next step. Choose your sign-up path below to
                                begin.
                            </p>
                        </div>
                    </div>
                </section>
                {/* Options Section */}
                <section className="flex flex-col items-center bg-paper px-4 py-10">
                    <p className="text-xl md:text-3xl text-navy font-semibold font-poppins mb-6">
                        Choose Your Path
                    </p>

                    <div className="flex flex-col justify-center w-full max-w-6xl gap-10 lg:flex-row">
                        {/* Investor Option */}
                        <div className="flex flex-col items-center w-full p-3 text-center rounded-lg bg-white shadow-md mb-6 lg:w-1/2 max-w-sm mx-auto lg:max-w-sm xl:max-w-md lg:p-6 xl:p-8 lg:mx-0">
                            <p className="text-base lg:text-lg xl:text-xl text-[#D4AF37] font-poppins mb-2 font-bold uppercase tracking-wide">
                                INVESTOR SIGN UP
                            </p>
                            <p className="text-sm lg:text-sm xl:text-base text-navy font-regular font-poppins mb-2">
                                View current Regulation Crowdfunding offerings.
                            </p>
                            <img
                                src="/images/investor.png"
                                alt="Investor Sign Up"
                                className="object-cover w-32 h-32 lg:w-40 lg:h-40 xl:w-48 xl:h-48 mb-3 rounded-lg mx-auto"
                            />
                            <p className="text-sm lg:text-sm xl:text-base text-navy font-regular font-poppins mb-2">
                                As an AQi Investor, you&apos;ll have access to:
                            </p>
                            <ul className="list-disc pl-6 pr-2 text-left text-sm lg:text-sm xl:text-base text-navy font-regular font-poppins mb-3 w-full max-w-[90%] mx-auto">
                                <li>Current Offerings</li>
                                <li>Transparent Investment Details</li>
                                <li>Investor Education</li>
                            </ul>
                            <button
                                onClick={handleInvestorClick}
                                className={`${accountActionClass} mt-2 text-xs tracking-wide lg:text-sm xl:text-base`}
                            >
                                START HERE
                            </button>
                        </div>

                        {/* Entrepreneur Option */}
                        <div className="flex flex-col items-center w-full p-3 text-center rounded-lg bg-white shadow-md mb-6 lg:w-1/2 max-w-sm mx-auto lg:max-w-sm xl:max-w-md lg:p-6 xl:p-8 lg:mx-0">
                            <p className="text-base lg:text-lg xl:text-xl text-[#D4AF37] font-poppins mb-2 font-bold uppercase tracking-wide">
                                ENTREPRENEUR SIGN UP
                            </p>
                            <p className="text-sm lg:text-sm xl:text-base text-navy font-regular font-poppins mb-2">
                                Start here to connect your business with the
                                capital it needs.
                            </p>
                            <img
                                src="/images/entrepreneur.png"
                                alt="Entrepreneur Sign Up"
                                className="object-cover w-32 h-32 lg:w-40 lg:h-40 xl:w-48 xl:h-48 mb-3 rounded-lg mx-auto"
                            />
                            <p className="text-sm lg:text-sm xl:text-base text-navy font-regular font-poppins mb-2">
                                As an AQi Entrepreneur, you&apos;ll be able to:
                            </p>
                            <ul className="list-disc pl-6 pr-2 text-left text-sm lg:text-sm xl:text-base text-navy font-regular font-poppins mb-3 w-full max-w-[90%] mx-auto">
                                <li>Apply to Raise Capital</li>
                                <li>Showcase Your Vision</li>
                                <li>Connect with Aligned Investors</li>
                            </ul>
                            <button
                                onClick={handleEntrepreneurClick}
                                className={`${accountActionClass} mt-2 text-xs tracking-wide lg:text-sm xl:text-base`}
                            >
                                START HERE
                            </button>
                        </div>
                    </div>
                </section>
            </div>
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black bg-opacity-50">
                    <div
                        className={
                            `bg-white rounded-lg shadow-xl p-6 text-center border border-navy relative ` +
                            (modalStep > 3 ? bigModal : smallModal)
                        }
                    >
                        {/* X Close Button */}
                        <button
                            className="absolute top-4 right-4 text-navy text-2xl font-bold hover:text-[#D4AF37] focus:outline-none"
                            onClick={() => setShowModal(false)}
                            aria-label="Close"
                        >
                            &times;
                        </button>
                        {modalStep <= 3 && (
                            <img
                                src="/images/AeQuitas-Logo-blue-with-text.png"
                                alt="AQI Logo"
                                className="w-40 mx-auto mb-4 md:w-52 lg:w-60"
                            />
                        )}
                        {modalStep === 1 && (
                            <>
                                <h2 className="text-lg md:text-xl text-navy font-medium font-poppins italic mt-4">
                                    Quick question before we dive in…
                                </h2>
                                <p className="text-lg md:text-xl text-navy font-medium font-poppins italic mt-4">
                                    Is your company at least
                                    <br />
                                    50% woman-owned?
                                </p>
                                <div className="flex flex-col items-center gap-3 mt-6">
                                    <button
                                        onClick={() => handleAnswer(true)}
                                        className={affirmativeOptionClass}
                                    >
                                        Yes, we’re at least 50% woman-owned
                                    </button>
                                    <button
                                        onClick={() => handleAnswer(false)}
                                        className={negativeOptionClass}
                                    >
                                        No, we don’t meet that requirement
                                    </button>
                                </div>
                            </>
                        )}

                        {modalStep === 2 && (
                            <>
                                <h2 className="text-2xl font-medium font-poppins italic text-navy mt-2">
                                    Great!
                                </h2>
                                <p className="text-2xl font-medium font-poppins italic text-navy mb-6">
                                    Let’s do this!
                                </p>
                                <button
                                    onClick={handleFinalContinue}
                                    className={primaryActionClass}
                                >
                                    CONTINUE
                                </button>
                            </>
                        )}

                        {modalStep === 3 && (
                            <>
                                <h2 className="text-xl font-medium font-poppins italic text-navy mb-1">
                                    Thank you for your interest in AQi.
                                </h2>
                                <p className="text-text-base md:lg font-medium font-poppins italic  text-navy leading-relaxed mt-4">
                                    At this time, our platform is dedicated to
                                    <br />
                                    supporting companies that are majority
                                    woman-owned.
                                    <br />
                                    Please check back for future updates or
                                    partner opportunities.
                                </p>
                                <div className="flex justify-center mt-6">
                                    <button
                                        onClick={handleGoHome}
                                        className={secondaryActionClass}
                                    >
                                        HOME
                                    </button>
                                </div>
                            </>
                        )}
                        {modalStep === 4 && (
                            <>
                                <div className="pl-4 text-left sm:pl-6 md:pl-8 lg:pl-10">
                                    <h2 className="text-2xl md:text-3xl font-semibold text-navy mb-4">
                                        Checklist for Getting Started
                                    </h2>

                                    <p className="text-left font-bold text-navy/90 mb-4">
                                        To ensure your company is eligible to
                                        raise capital on AQi, we required to
                                        confirm that you meet U.S. regulatory
                                        requirements for crowdfunding under
                                        Reguilation CF.
                                    </p>
                                    <p className="text-left font-bold text-navy/90 mb-4">
                                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;To
                                        continue your application, please
                                        confirm that you can answer yes to all
                                        of the following about your company:
                                    </p>

                                    <ul className="text-left space-y-1 text-navy list-disc list-inside">
                                        <li>is at least 50 % woman-owned.</li>
                                        <li>
                                            is organized and operating in the
                                            United States and is not
                                            foreign-owned.
                                        </li>
                                        <li>
                                            has not raised more than $5 million
                                            under Reg CF in the past 12 months.
                                        </li>
                                        <li>
                                            If previously conducted a Reg CF
                                            offering, it has filed all required
                                            reports(eg., Form C-AR).
                                        </li>
                                        <li>
                                            Neither the Company nor any of its
                                            officers, directors, general
                                            partners, or 20% beneficial owners
                                            are subject to &quot;bad actor&quot;
                                            disqualifications under SEC rules.
                                        </li>
                                        <li>
                                            is not an investment company or
                                            subject to the investment Company
                                            Act of 1940.
                                        </li>
                                        <li>
                                            has a current business plan and
                                            intended use of funds.
                                        </li>
                                        <li>
                                            is seeking to raise between $150,000
                                            and $5,000,000 in capital.
                                        </li>
                                    </ul>
                                </div>
                                <button
                                    onClick={handleChecklistYes}
                                    className={`${primaryActionClass} mt-6`}
                                >
                                    YES
                                </button>

                                <p className="mt-3 text-sm italic">
                                    If you cannot answer YES to all of the
                                    above, please{" "}
                                    <span
                                        className="text-navy underline cursor-pointer"
                                        onClick={handleGoHome}
                                    >
                                        EXIT
                                    </span>
                                </p>
                            </>
                        )}
                        {modalStep === 5 && (
                            <EntrepreneurTermsNConditions
                                onBack={() => setModalStep(4)}
                                onClose={() => setShowModal(false)}
                                onSuccess={() => {
                                    // "Continue" in T&C
                                    setShowModal(false);
                                    navigate("/entrepreneur-application");
                                }}
                            />
                        )}
                    </div>
                </div>
            )}

            {showInvestorModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black bg-opacity-50">
                    <div
                        className={
                            `bg-white rounded-lg shadow-xl p-6 text-center border border-navy relative ` +
                            (investorModalStep === 4 ? RegModal : smallModal)
                        }
                    >
                        {/* X Close Button - always visible */}
                        <button
                            style={{ top: 16, right: 16, zIndex: 100 }}
                            className="absolute text-navy text-2xl font-bold hover:text-[#D4AF37] focus:outline-none"
                            onClick={() => setShowInvestorModal(false)}
                            aria-label="Close"
                        >
                            &times;
                        </button>
                        {investorModalStep != 4 && (
                            <img
                                src="/images/AeQuitas-Logo-blue-with-text.png"
                                alt="AQI Logo"
                                className="w-40 mx-auto mb-4 md:w-52 lg:w-60"
                            />
                        )}

                        {investorModalStep === 1 && (
                            <>
                                <h2 className="text-lg md:text-xl text-navy font-medium font-poppins italic mt-4">
                                    To invest on this platform, you must be a
                                    U.S. citizen.
                                </h2>

                                <div className="flex flex-col items-center gap-3 mt-6">
                                    <button
                                        onClick={() => handleAnswer1(true)}
                                        className={affirmativeOptionClass}
                                    >
                                        Yes, I am a U.S. citizen.
                                    </button>
                                    <button
                                        onClick={() => handleAnswer1(false)}
                                        className={negativeOptionClass}
                                    >
                                        No, I don’t meet that requirement
                                    </button>
                                </div>
                            </>
                        )}
                        {investorModalStep === 2 && (
                            <>
                                <h2 className="text-2xl font-medium font-poppins italic text-navy mt-2">
                                    Great!
                                </h2>
                                <p className="text-2xl font-medium font-poppins italic text-navy mb-6">
                                    Let’s do this!
                                </p>
                                <button
                                    onClick={() => {
                                        setShowInvestorModal(false);
                                        navigate("/investor-welcome");
                                    }}
                                    className={primaryActionClass}
                                >
                                    CONTINUE
                                </button>
                            </>
                        )}

                        {investorModalStep === 3 && (
                            <>
                                <h2 className="text-2xl font-medium font-poppins italic text-navy mt-2">
                                    Sorry, You&apos;re Not Eligible to Invest.
                                </h2>
                                <p className="text-base font-medium font-poppins italic text-navy leading-relaxed mt-4">
                                    At this time, our platform is only open to U.S. citizens with a valid U.S. address due to regulatory requirements.
                                    <br />
                                    Please check back for future updates or partner opportunities.
                                </p>
                                <div className="flex justify-center mt-6">
                                    <button
                                        onClick={() => setShowInvestorModal(false)}
                                        className={secondaryActionClass}
                                    >
                                        HOME
                                    </button>
                                </div>
                            </>
                        )}
                        {investorModalStep === 4 && (
                            <InvestorConsent
                                onClose={() => setShowInvestorModal(false)}
                                // "BACK" in T&C => checklist
                                onSuccess={() => {
                                    // "Continue" in T&C
                                    setShowInvestorModal(false);
                                    navigate("/investor-application");
                                }}
                            />
                        )}
                    </div>
                </div>
            )}

            <Footer />
        </>
    );
};

export default LandingPage;
